import { adminPath, marketingHome } from '../domains';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { PageTitle } from './PageTitle';
import { Wordmark } from './Wordmark';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  adminOnly?: boolean;
  /** Shows the open safety-report count beside the label. */
  safetyBadge?: boolean;
}

/** Safety reports alert staff by push too; this catches whoever is at a desk. */
const SAFETY_POLL_MS = 60_000;

/**
 * Open safety reports, refreshed every minute while the console is in
 * front of someone and again on each navigation — so resolving the last
 * one clears the badge as soon as you move on. `null` until the first
 * answer, and left alone on a failed poll rather than flashing to zero.
 */
function useOpenSafetyCount(): number | null {
  const { pathname } = useLocation();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .get<{ open: number }>('/admin/safety-incidents/open-count')
        .then((res) => {
          if (!cancelled) setCount(res.data.open);
        })
        .catch(() => {});
    };
    refresh();
    const timer = window.setInterval(refresh, SAFETY_POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [pathname]);

  return count;
}

const NAV: NavItem[] = [
  { to: adminPath('/admin'), label: 'Dashboard', end: true },
  { to: adminPath('/admin/chats'), label: 'Inbox' },
  { to: adminPath('/admin/safety'), label: 'Safety reports', safetyBadge: true },
  { to: adminPath('/admin/people'), label: 'People' },
  { to: adminPath('/admin/map'), label: 'Map' },
  { to: adminPath('/admin/rides'), label: 'Rides' },
  { to: adminPath('/admin/parcels'), label: 'Parcels' },
  { to: adminPath('/admin/papalit'), label: 'Papalit' },
  { to: adminPath('/admin/food-orders'), label: 'Food & store orders' },
  { to: adminPath('/admin/drivers'), label: 'Drivers' },
  { to: adminPath('/admin/merchants'), label: 'Merchants' },
  { to: adminPath('/admin/payouts'), label: 'Payouts' },
  { to: adminPath('/admin/remittances'), label: 'Remittances' },
  { to: adminPath('/admin/payments'), label: 'Payments' },
  { to: adminPath('/admin/refunds'), label: 'Refunds' },
  { to: adminPath('/admin/disputes'), label: 'Item disputes' },
  { to: adminPath('/admin/moderation'), label: 'Reported content' },
  { to: adminPath('/admin/applications'), label: 'Applications' },
  { to: adminPath('/admin/staff'), label: 'Staff', adminOnly: true },
  { to: adminPath('/admin/audit'), label: 'Audit log', adminOnly: true },
];

export function AppLayout() {
  const { user, userDoc, isAdmin, signOut } = useAuth();
  const visibleNav = NAV.filter((item) => !item.adminOnly || isAdmin);
  const openSafety = useOpenSafetyCount();

  return (
    <div className="flex min-h-screen bg-paper">
      <PageTitle>dala console</PageTitle>
      <aside className="flex w-60 flex-col bg-ink text-white">
        <div className="px-6 py-6">
          <Wordmark to={marketingHome} tone="onInk" size="sm" />
          <div className="mt-2 h-1 w-8 rounded-full bg-mango" />
          <div className="overline mt-3 !text-mango">Console</div>
        </div>
        <nav className="flex-1 px-3">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                'mb-1 block rounded-tile px-3 py-2 text-sm font-bold ' +
                (isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/60 hover:bg-white/10 hover:text-white')
              }
            >
              {item.label}
              {item.safetyBadge && openSafety != null && openSafety > 0 && (
                <span
                  className="ml-2 inline-block min-w-[1.25rem] rounded-full bg-danger px-1.5 text-center text-xs font-extrabold text-white"
                  aria-label={`${openSafety} open`}
                >
                  {openSafety}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-4 py-4 text-sm">
          <div className="truncate font-bold">
            {userDoc?.displayName ?? user?.email ?? 'Staff'}
          </div>
          <div className="truncate text-xs font-semibold text-white/50">
            {userDoc?.role ?? '—'}
          </div>
          <button
            className="mt-3 text-xs font-bold text-mango hover:underline"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
}
